import React, { useState, useMemo } from 'react';
import './LeaderboardDisplay.css';

const LeaderboardDisplay = ({ data, timeframe }) => {
  const [sortBy, setSortBy] = useState('averageReturn'); // 'averageReturn', 'totalAUM', 'totalTrades'
  const [showDetails, setShowDetails] = useState(new Set());

  // Process and sort leaderboard data
  const processedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const sorted = [...data].sort((a, b) => {
      switch (sortBy) {
        case 'totalAUM':
          return b.totalAUM - a.totalAUM;
        case 'totalTrades':
          return b.totalTrades - a.totalTrades;
        case 'averageReturn':
        default:
          return b.averageReturn - a.averageReturn;
      }
    });

    // Recalculate ranks based on current sort
    return sorted.map((manager, index) => ({
      ...manager,
      currentRank: index + 1
    }));
  }, [data, sortBy]);

  // Toggle manager details
  const toggleDetails = (managerName) => {
    const newShowDetails = new Set(showDetails);
    if (newShowDetails.has(managerName)) {
      newShowDetails.delete(managerName);
    } else {
      newShowDetails.add(managerName);
    }
    setShowDetails(newShowDetails);
  };

  // Format currency amounts
  const formatSOL = (amount) => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return '0.00 SOL';
    if (numAmount >= 1000000) {
      return `${(numAmount / 1000000).toFixed(2)}M SOL`;
    }
    if (numAmount >= 1000) {
      return `${(numAmount / 1000).toFixed(2)}K SOL`;
    }
    return `${numAmount.toFixed(2)} SOL`;
  };

  // Format percentage
  const formatPercentage = (value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '0.00%';
    return `${numValue >= 0 ? '+' : ''}${numValue.toFixed(2)}%`;
  };

  // Format experience duration
  const formatExperience = (dateStr) => {
    const startDate = new Date(dateStr);
    const now = new Date();
    const diffMs = now - startDate;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffYears > 0) {
      return `${diffYears}y ${diffMonths % 12}m`;
    }
    if (diffMonths > 0) {
      return `${diffMonths}m`;
    }
    return `${diffDays}d`;
  };

  // Get rank medal/icon
  const getRankIcon = (rank) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return `#${rank}`;
    }
  };

  // Get performance color
  const getPerformanceColor = (value) => {
    if (value >= 20) return '#10B981'; // Green for excellent
    if (value >= 10) return '#84CC16'; // Light green for good
    if (value >= 0) return '#6B7280'; // Gray for neutral
    if (value >= -10) return '#F59E0B'; // Orange for poor
    return '#EF4444'; // Red for very poor
  };

  if (!data || data.length === 0) {
    return (
      <div className="leaderboard-display empty">
        <div className="empty-state">
          <h4>No Manager Data Available</h4>
          <p>Pool manager performance data will appear here once pools have trading history.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="leaderboard-display">
      <div className="leaderboard-header">
        <div className="header-info">
          <h3>Manager Performance Leaderboard</h3>
          <p>Ranking based on {timeframe} performance</p>
        </div>

        <div className="sort-controls">
          <label>Sort by:</label>
          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            className="sort-select"
          >
            <option value="averageReturn">Average Return</option>
            <option value="totalAUM">Assets Under Management</option>
            <option value="totalTrades">Trading Activity</option>
          </select>
        </div>
      </div>

      <div className="leaderboard-list">
        {processedData.map((manager, index) => (
          <div 
            key={manager.managerName}
            className={`leaderboard-item rank-${manager.currentRank <= 3 ? manager.currentRank : 'other'}`}
            onClick={() => toggleDetails(manager.managerName)}
          >
            <div className="manager-main">
              <div className="rank-section">
                <div className="rank-display">
                  {getRankIcon(manager.currentRank)}
                </div>
                {manager.currentRank <= 3 && (
                  <div className="rank-badge">
                    Top {manager.currentRank}
                  </div>
                )}
              </div>

              <div className="manager-info">
                <div className="manager-identity">
                  <span className="manager-name">
                    {manager.managerName}
                    {manager.verified && (
                      <span className="verified-badge" title="Verified Manager">
                        ✓
                      </span>
                    )}
                  </span>
                  <span className="manager-experience">
                    {formatExperience(manager.experienceSince)} experience
                  </span>
                </div>
                
                <div className="manager-stats">
                  <div className="stat-item">
                    <span className="stat-label">Pools:</span>
                    <span className="stat-value">{manager.poolsManaged}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">AUM:</span>
                    <span className="stat-value">{formatSOL(manager.totalAUM)}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Trades:</span>
                    <span className="stat-value">{manager.totalTrades}</span>
                  </div>
                </div>
              </div>

              <div className="performance-section">
                <div 
                  className="performance-value"
                  style={{ color: getPerformanceColor(manager.averageReturn) }}
                >
                  {formatPercentage(manager.averageReturn)}
                </div>
                <div className="performance-label">
                  Avg Return ({timeframe})
                </div>
              </div>

              <div className="expand-indicator">
                {showDetails.has(manager.managerName) ? '▼' : '▶'}
              </div>
            </div>

            {showDetails.has(manager.managerName) && (
              <div className="manager-details">
                <div className="details-grid">
                  <div className="detail-section">
                    <h5>Performance Metrics</h5>
                    <div className="detail-items">
                      <div className="detail-item">
                        <span className="detail-label">Average Return:</span>
                        <span 
                          className="detail-value"
                          style={{ color: getPerformanceColor(manager.averageReturn) }}
                        >
                          {formatPercentage(manager.averageReturn)}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Total AUM:</span>
                        <span className="detail-value">
                          {formatSOL(manager.totalAUM)}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Total Trades:</span>
                        <span className="detail-value">
                          {manager.totalTrades.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h5>Pool Management</h5>
                    <div className="detail-items">
                      <div className="detail-item">
                        <span className="detail-label">Pools Managed:</span>
                        <span className="detail-value">
                          {manager.poolsManaged}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Avg Management Fee:</span>
                        <span className="detail-value">
                          {parseFloat(manager.averageManagementFee || 0).toFixed(2)}%
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Avg Performance Fee:</span>
                        <span className="detail-value">
                          {parseFloat(manager.averagePerformanceFee || 0).toFixed(2)}%
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h5>Experience</h5>
                    <div className="detail-items">
                      <div className="detail-item">
                        <span className="detail-label">First Pool Created:</span>
                        <span className="detail-value">
                          {new Date(manager.experienceSince).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Experience:</span>
                        <span className="detail-value">
                          {formatExperience(manager.experienceSince)}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Verified Status:</span>
                        <span className="detail-value">
                          {manager.verified ? (
                            <span className="verified-text">✓ Verified</span>
                          ) : (
                            <span className="unverified-text">Not Verified</span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="manager-insights">
                  <h5>Performance Insights</h5>
                  <div className="insights-grid">
                    <div className="insight-item">
                      <span className="insight-label">AUM per Pool:</span>
                      <span className="insight-value">
                        {formatSOL(manager.totalAUM / manager.poolsManaged)}
                      </span>
                    </div>
                    <div className="insight-item">
                      <span className="insight-label">Trades per Pool:</span>
                      <span className="insight-value">
                        {Math.round(manager.totalTrades / manager.poolsManaged)}
                      </span>
                    </div>
                    <div className="insight-item">
                      <span className="insight-label">Fee Structure:</span>
                      <span className="insight-value">
                        {parseFloat(manager.averageManagementFee || 0).toFixed(1)}% + {parseFloat(manager.averagePerformanceFee || 0).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="leaderboard-footer">
        <div className="leaderboard-summary">
          <div className="summary-stats">
            <div className="summary-stat">
              <span className="summary-label">Total Managers:</span>
              <span className="summary-value">{processedData.length}</span>
            </div>
            <div className="summary-stat">
              <span className="summary-label">Verified Managers:</span>
              <span className="summary-value">
                {processedData.filter(m => m.verified).length}
              </span>
            </div>
            <div className="summary-stat">
              <span className="summary-label">Total AUM:</span>
              <span className="summary-value">
                {formatSOL(processedData.reduce((sum, m) => sum + m.totalAUM, 0))}
              </span>
            </div>
            <div className="summary-stat">
              <span className="summary-label">Avg Platform Return:</span>
              <span 
                className="summary-value"
                style={{ 
                  color: getPerformanceColor(
                    processedData.reduce((sum, m) => sum + m.averageReturn, 0) / processedData.length
                  )
                }}
              >
                {formatPercentage(
                  processedData.reduce((sum, m) => sum + m.averageReturn, 0) / processedData.length
                )}
              </span>
            </div>
          </div>
        </div>

        <div className="leaderboard-disclaimer">
          <p>
            <small>
              Rankings are based on {timeframe} performance. Past performance does not guarantee future results.
              Manager verification indicates identity confirmation, not performance guarantee.
            </small>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LeaderboardDisplay;